import { lazy } from 'react';
import { getRulerMeasurementConfigPublishWarnings } from './uis/RulerMeasurementUI/configPublishWarnings';
import { getSlideLevelClassificationConfigPublishWarnings } from './uis/SlideLevelClassificationUI/configPublishWarnings';
import { getSegmentationConfigPublishWarnings } from './uis/SegmentationUI/configPublishWarnings';
import { getBoundingBoxConfigPublishWarnings } from './uis/BoundingBoxUI/configPublishWarnings';
import { getPointConfigPublishWarnings } from './uis/PointUI/configPublishWarnings';
import { getSlideLevelClassificationPublishBlockingReasons } from './uis/SlideLevelClassificationUI/slideLevelClassificationModel';
import { getItemMetadataConfigPublishWarnings } from './itemMetadata/itemMetadataModel';

/**
 * UI_REGISTRY — the single source of truth for all annotation UI modules.
 *
 * All loading goes through this registry. The rest of the app calls getUI(),
 * getUITopBarControls(), or getUIConfigForm() and never imports UIs directly.
 * This boundary means that migrating from React lazy to Module Federation
 * requires changing only this file.
 *
 * Both `component` and `configForm` use lazy() so the shape of each entry is
 * identical to what a federation loadRemoteModule() call would produce.
 *
 * --------------------------------------------------------------------------
 * UI MODULE CONTRACT (§8.7)
 * --------------------------------------------------------------------------
 * Each UI module must export:
 *
 *   default function UIComponent(props)
 *     Props:
 *       projectConfig        - full girderProjects object
 *       uiConfig             - annotationUI.config sub-object
 *       items                - array of Girder item docs for this project
 *       currentItem          - the currently active item doc
 *       folderId             - project folder ID (for annotation API calls)
 *       onCurrentItemChange  - callback(newItem) when user navigates items
 *       onComplete           - callback when user marks work complete
 *       onSaveStatusChange   - callback('idle'|'saving'|'saved'|'error')
 *                              called whenever the UI's internal save state changes.
 *                              ProjectViewer stores this and passes it to ViewerTopBar.
 *       onCompletableChange  - (deprecated for TopBarControls) use onAnnotationCompletionStatusChange
 *       onAnnotationCompletionStatusChange - callback(AnnotationCompletionStatus) from ./completionPolicy.js
 *                              completable, strictComplete, incompleteReasons, incompleteReasonCodes.
 *                              ProjectViewer forwards to ViewerTopBar for Complete button + confirm dialog.
 *       activateSlotRef      - optional; ruler UI attaches activateSlot(slotId). Other UIs may omit.
 *
 *   export function ConfigForm(props)   — admin UI for configuring this UI in ProjectDetail
 *   export { default as metadata }      — { id, label, description, version }
 *
 * Optional exports:
 *
 *   export function TopBarControls(props)
 *     itemMetadataPlacement — optional 'left' | 'right' (default 'right' in viewers): after item nav
 *       vs inside ViewerTopBar taskControls after the UI controls (wraps with task row when narrow).
 *
 *   export function TopBarControls(props)
 *     A React component rendered by ProjectViewer into ViewerTopBar's taskControls slot.
 *     Receives: { uiConfig, folderId, itemId, currentItem?, toolkitRef, toolkitReady, completed,
 *                 onSaveStatusChange, onAnnotationCompletionStatusChange, ...
 *                 onToolkitLayersUpdated?: () => void — call after merging GeoJSON into the toolkit
 *                     (ROI/user layers) so shared viewer chrome (ROI navigator) can refresh. }
 *     Computes completion status; no viewer-specific logic in ProjectViewer.
 *     If a UI exports no TopBarControls, the taskControls slot in ViewerTopBar is empty.
 *
 * Progress export (admin Progress page + GET projects/:id/progress/export):
 *   - Persist girderProjects.exportData in the shape expected by the server handler for
 *     metadata.id (Python: girder_projects/resources/progress_export_handlers.py EXPORT_HANDLERS).
 *   - Ruler: exportData.measurements[].  Bounding box: exportData.boxes[] (center, width,
 *     height, rotation radians, categoryId, categoryLabel — base image coordinates).
 *   - GET projects/:id/progress/all includes exportCapabilities { annotationUIId, layouts }.
 *     Layouts are driven by the handler’s supported_layouts (e.g. wide CSV only when layouts
 *     includes "wide"). Register a new UI id in both UI_REGISTRY and EXPORT_HANDLERS together.
 *
 * Config publish warnings (Project status → Before publishing):
 *   Optional sidecar module per UI, e.g. uis/<UI>/configPublishWarnings.js, exporting a pure
 *   function (annotationUI) => string[]. Register in CONFIG_PUBLISH_WARNINGS_BY_UI_ID below.
 *   ProjectDetail merges these with core warnings via getAnnotationUiConfigPublishWarnings().
 *
 * Full onboarding (diagram, TopBarControls props, Python checklist): ./ANNOTATION_UI.md
 * --------------------------------------------------------------------------
 */

/** @type {Record<string, (annotationUI: { id?: string, config?: object }) => string[]>} */
const CONFIG_PUBLISH_WARNINGS_BY_UI_ID = {
    'ruler-measurement': getRulerMeasurementConfigPublishWarnings,
    'slide-level-classification': getSlideLevelClassificationConfigPublishWarnings,
    segmentation: getSegmentationConfigPublishWarnings,
    'bounding-box': getBoundingBoxConfigPublishWarnings,
    point: getPointConfigPublishWarnings,
};

const CONFIG_PUBLISH_BLOCKS_BY_UI_ID = {
    'slide-level-classification': (annotationUI) =>
        getSlideLevelClassificationPublishBlockingReasons(annotationUI?.config),
};

const UI_REGISTRY = {
    'ruler-measurement': {
        component: lazy(() => import('./uis/RulerMeasurementUI/index')),
        topBarControls: lazy(() =>
            import('./uis/RulerMeasurementUI/index').then((m) => ({ default: m.TopBarControls }))
        ),
        configForm: lazy(() =>
            import('./uis/RulerMeasurementUI/index').then((m) => ({ default: m.ConfigForm }))
        ),
        metadata: {
            id: 'ruler-measurement',
            label: 'Ruler Measurement',
            description: 'Collect labelled ruler measurements from slides.',
            version: '1.0.0',
            taskSectionLabel: 'Measurements:',
            itemMetadataPlacement: 'right',
        },
        supportsProjectRoi: true,
    },
    'bounding-box': {
        component: lazy(() => import('./uis/BoundingBoxUI/index')),
        topBarControls: lazy(() =>
            import('./uis/BoundingBoxUI/index').then((m) => ({ default: m.TopBarControls }))
        ),
        configForm: lazy(() =>
            import('./uis/BoundingBoxUI/index').then((m) => ({ default: m.ConfigForm }))
        ),
        metadata: {
            id: 'bounding-box',
            label: 'Bounding Box',
            description: 'Draw rectangles around objects by category.',
            version: '1.0.0',
            deleteInTaskControls: true,
            taskSectionLabel: 'Bounding boxes:',
            itemMetadataPlacement: 'right',
        },
        supportsProjectRoi: true,
    },
    'slide-level-classification': {
        component: lazy(() => import('./uis/SlideLevelClassificationUI/index')),
        topBarControls: lazy(() =>
            import('./uis/SlideLevelClassificationUI/index').then((m) => ({ default: m.TopBarControls }))
        ),
        configForm: lazy(() =>
            import('./uis/SlideLevelClassificationUI/index').then((m) => ({ default: m.ConfigForm }))
        ),
        metadata: {
            id: 'slide-level-classification',
            label: 'Slide-level classification',
            description: 'One or more questions per slide (categories and/or free-text); optional CSV metadata by filename.',
            version: '1.0.0',
            taskSectionLabel: 'Questions:',
            itemMetadataPlacement: 'right',
        },
        supportsProjectRoi: true,
    },
    segmentation: {
        component: lazy(() => import('./uis/SegmentationUI/index')),
        topBarControls: lazy(() =>
            import('./uis/SegmentationUI/index').then((m) => ({ default: m.TopBarControls }))
        ),
        configForm: lazy(() =>
            import('./uis/SegmentationUI/index').then((m) => ({ default: m.ConfigForm }))
        ),
        metadata: {
            id: 'segmentation',
            label: 'Segmentation',
            description: 'Multi-class polygon segmentation with boolean overlap tools.',
            version: '1.0.0',
            taskSectionLabel: 'Segmentation:',
            taskSectionLabelMode: 'sr-only',
            deleteInTaskControls: true,
            clearSelectedInResetMenu: true,
            itemMetadataPlacement: 'right',
        },
        supportsProjectRoi: true,
    },
    point: {
        component: lazy(() => import('./uis/PointUI/index')),
        topBarControls: lazy(() =>
            import('./uis/PointUI/index').then((m) => ({ default: m.TopBarControls }))
        ),
        configForm: lazy(() =>
            import('./uis/PointUI/index').then((m) => ({ default: m.ConfigForm }))
        ),
        metadata: {
            id: 'point',
            label: 'Point',
            description: 'Drop point annotations by category.',
            version: '1.0.0',
            deleteInTaskControls: true,
            taskSectionLabel: 'Points:',
            itemMetadataPlacement: 'right',
        },
        supportsProjectRoi: true,
    },
};

export function getUI(id) {
    return UI_REGISTRY[id]?.component ?? null;
}

/** Returns the optional TopBarControls lazy component for a UI, or null. */
export function getUITopBarControls(id) {
    return UI_REGISTRY[id]?.topBarControls ?? null;
}

export function getUIConfigForm(id) {
    return UI_REGISTRY[id]?.configForm ?? null;
}

/** Returns metadata for a UI (id, label, description, version, deleteInTaskControls, clearSelectedInResetMenu, etc.). */
export function getUIMetadata(id) {
    return UI_REGISTRY[id]?.metadata ?? null;
}

/** True if this annotation UI uses project-level ROI overlay configuration. */
export function getUISupportsProjectRoi(id) {
    return Boolean(id && UI_REGISTRY[id]?.supportsProjectRoi);
}

/**
 * UI-specific strings for Project status (draft, saved settings). Core checks (no UI id, no
 * items, ROI flags) stay in ProjectDetail; this only dispatches by annotationUI.id.
 */
export function getAnnotationUiConfigPublishWarnings(annotationUI) {
    if (!annotationUI?.id) return [];
    const fn = CONFIG_PUBLISH_WARNINGS_BY_UI_ID[annotationUI.id];
    const uiOut = typeof fn === 'function' ? fn(annotationUI) : [];
    const uiWarnings = Array.isArray(uiOut)
        ? uiOut.filter((s) => typeof s === 'string' && s.trim().length > 0)
        : [];
    const metaWarnings = getItemMetadataConfigPublishWarnings(annotationUI);
    return [...uiWarnings, ...metaWarnings];
}

export function getAnnotationUiConfigPublishBlocks(annotationUI) {
    if (!annotationUI?.id) return [];
    const fn = CONFIG_PUBLISH_BLOCKS_BY_UI_ID[annotationUI.id];
    if (typeof fn !== 'function') return [];
    const out = fn(annotationUI);
    return Array.isArray(out)
        ? out.filter((s) => typeof s === 'string' && s.trim().length > 0)
        : [];
}

export function listUIs() {
    return Object.values(UI_REGISTRY).map((entry) => entry.metadata);
}
